/**
 * PwaPrompts: the install + update affordances for the offline-capable app.
 *   • Install: captures `beforeinstallprompt`, offers an Install chip, and calls
 *     the deferred prompt on click.
 *   • Update: watches the service-worker registration; when a new build has
 *     installed and is waiting, offers a "reload to update" chip (skip-waiting +
 *     reload). The SW is PROD-only, so neither fires under `vite dev`.
 *   • iOS install hint: Safari never fires `beforeinstallprompt`, so on iOS
 *     (and not already installed) show dismissible Share-sheet instructions
 *     instead; the dismissal persists so it only shows once.
 * Nothing here touches the network beyond the SW the app already registers.
 */
import { useEffect, useState } from "react";
import { storageKey } from "../../utils/storageKeys";
import { useT, type Locale } from "../i18n";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const IOS_HINT_KEY = storageKey("iosInstallHintDismissed");
const UPDATE_DISMISS_KEY = storageKey("updateChipDismissed");

/** True when THIS waiting worker's chip was dismissed this session. A
 *  different waiting worker (a newer build) re-shows the chip. */
function isUpdateDismissed(scriptUrl: string | undefined): boolean {
  if (!scriptUrl) return false;
  try {
    return sessionStorage.getItem(UPDATE_DISMISS_KEY) === scriptUrl;
  } catch {
    return false;
  }
}

/** Detects iOS Safari (incl. iPadOS 13+, which masquerades as a Mac). */
export function isIosUserAgent(
  nav: Pick<Navigator, "userAgent" | "platform" | "maxTouchPoints">,
): boolean {
  return (
    /iPad|iPhone|iPod/.test(nav.userAgent) ||
    (nav.platform === "MacIntel" && nav.maxTouchPoints > 1)
  );
}

/** Detects an already-installed (standalone) launch, iOS's non-standard flag included. */
export function isStandaloneDisplay(
  win: Pick<Window, "matchMedia">,
  nav: { standalone?: boolean },
): boolean {
  return win.matchMedia("(display-mode: standalone)").matches || nav.standalone === true;
}

/** `navigator.standalone` is an iOS-only extension absent from lib.dom's Navigator type. */
type NavigatorWithIosStandalone = Navigator & { standalone?: boolean };

const COPY: Record<
  Locale,
  { update: string; install: string; iosInstall: string; dismiss: string }
> = {
  en: {
    update: "A new version is ready. Reload to update",
    install: "Install MIME Smith. Runs offline, on your device",
    iosInstall:
      "Add MIME Smith to your home screen. In Safari, tap Share, then Add to Home Screen",
    dismiss: "Dismiss",
  },
  de: {
    update: "Eine neue Version ist da. Zum Aktualisieren neu laden",
    install: "MIME Smith installieren. Läuft offline, auf deinem Gerät",
    iosInstall:
      "MIME Smith zum Home-Bildschirm hinzufügen. Tippe in Safari auf Teilen, dann auf Zum Home-Bildschirm",
    dismiss: "Schließen",
  },
};

export function PwaPrompts() {
  const { locale } = useT();
  const c = COPY[locale] ?? COPY.en;
  const [installEvt, setInstallEvt] = useState<BeforeInstallPromptEvent | null>(null);
  const [updateReady, setUpdateReady] = useState(false);
  const [waitingUrl, setWaitingUrl] = useState<string | undefined>(undefined);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (typeof navigator === "undefined" || typeof window === "undefined") return;
    if (!isIosUserAgent(navigator)) return;
    if (isStandaloneDisplay(window, navigator as NavigatorWithIosStandalone)) return;
    try {
      if (localStorage.getItem(IOS_HINT_KEY)) return;
    } catch {
      /* private mode: fall through and show the hint anyway */
    }
    setShowIosHint(true);
  }, []);

  useEffect(() => {
    const onBip = (e: Event) => {
      e.preventDefault();
      setInstallEvt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setInstallEvt(null);
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    let reg: ServiceWorkerRegistration | null = null;
    let cancelled = false;
    const watch = (worker: ServiceWorker | null) => {
      if (!worker) return;
      worker.addEventListener("statechange", () => {
        if (worker.state === "installed" && navigator.serviceWorker.controller) {
          setWaitingUrl(worker.scriptURL);
          setUpdateReady(!isUpdateDismissed(worker.scriptURL));
        }
      });
    };
    const onUpdateFound = () => watch(reg?.installing ?? null);
    void navigator.serviceWorker.getRegistration().then((r) => {
      if (cancelled || !r) return;
      reg = r;
      if (r.waiting && navigator.serviceWorker.controller) {
        setWaitingUrl(r.waiting.scriptURL);
        setUpdateReady(!isUpdateDismissed(r.waiting.scriptURL));
      }
      r.addEventListener("updatefound", onUpdateFound);
    });
    return () => {
      cancelled = true;
      reg?.removeEventListener("updatefound", onUpdateFound);
    };
  }, []);

  const install = () => {
    if (!installEvt) return;
    void installEvt.prompt().finally(() => setInstallEvt(null));
  };
  const reload = () => {
    void navigator.serviceWorker?.getRegistration().then((r) => {
      r?.waiting?.postMessage({ type: "SKIP_WAITING" });
      window.location.reload();
    });
  };
  const dismissUpdate = () => {
    setUpdateReady(false);
    try {
      if (waitingUrl) sessionStorage.setItem(UPDATE_DISMISS_KEY, waitingUrl);
    } catch {
      /* private mode: the dismissal lasts until remount */
    }
  };
  const dismissIosHint = () => {
    setShowIosHint(false);
    try {
      localStorage.setItem(IOS_HINT_KEY, "1");
    } catch {
      /* private mode, ignore */
    }
  };

  // Permanently-mounted live region, prompts toggle inside it (screen readers
  // only announce insertions into an already-rendered region).
  const showAny = Boolean(installEvt || updateReady || showIosHint);
  return (
    <div role="status" aria-live="polite">
      {!showAny ? null : (
        <div className="nx-pwa">
          {updateReady ? (
            <div className="nx-pwa__row">
              <button type="button" className="nx-pwa__btn" onClick={reload}>
                <span className="nx-pwa__dot" aria-hidden="true" />
                {c.update}
              </button>
              <button
                type="button"
                className="nx-pwa__close"
                onClick={dismissUpdate}
                aria-label={c.dismiss}
              >
                ✕
              </button>
            </div>
          ) : installEvt ? (
            <button type="button" className="nx-pwa__btn" onClick={install}>
              <span className="nx-pwa__dot" aria-hidden="true" />
              {c.install}
            </button>
          ) : showIosHint ? (
            <div className="nx-pwa__row">
              <span className="nx-pwa__dot" aria-hidden="true" />
              <span className="nx-pwa__text">{c.iosInstall}</span>
              <button
                type="button"
                className="nx-pwa__close"
                onClick={dismissIosHint}
                aria-label={c.dismiss}
              >
                ✕
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
